import { useMemo, Dispatch, forwardRef, SetStateAction, useState } from 'react'
import {
  Box,
  ClickAwayListener,
  Typography,
  FormControl,
  FormLabel,
  FormGroup,
  FormControlLabel,
  Checkbox
} from '@mui/material'
import { FilterAltOutlined } from '@mui/icons-material'
import { useAutocomplete } from '@mui/base/useAutocomplete'
import theme from 'theme'
import { getActiveThemeMode } from 'theme/themeMode'

/* Utils */
import { ConfiguredDataSource, IntegrationInstance, LibraryFilters } from 'models'

/* Components */
import { ThemeButton } from 'ui/components/buttons'
import { Popper, PopperInner } from './filterCategoryCpis.styles'
import ResetIcon from 'ui/components/svg/ResetIcon'
import SearchDataSources from './search-data-sources'
import { InstanceCheckedIcon } from 'ui/components/svg'

type Ref = HTMLButtonElement | null

const CheckedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="9" fill={theme.colors.cta} stroke={theme.colors.cta} strokeWidth="2" />
    <circle cx="10" cy="10" r="5" fill={getActiveThemeMode() === 'light' ? '#ffffff' : theme.baseColors.primary[20]} />
  </svg>
)

const UncheckedIcon = () => (
  <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 20 20" fill="none">
    <circle cx="10" cy="10" r="9" stroke={theme.colors.controlBorder} strokeWidth="2" />
  </svg>
)

interface Props {
  filtersOpen: boolean
  handleCloseFilters: () => void
  handleClickFilter: () => void
  cpiActivationStatus: 'all' | 'active' | 'inactive'
  setCpiActivationStatus: Dispatch<SetStateAction<'all' | 'active' | 'inactive'>>
  cpiAssignment: 'all' | 'assigned' | 'unassigned'
  setCpiAssignment: Dispatch<SetStateAction<'all' | 'assigned' | 'unassigned'>>
  configuredIntegrations: ConfiguredDataSource[]
  setFilters: Dispatch<SetStateAction<LibraryFilters>>
  defaultFilters: LibraryFilters
  supportedIntegrations: IntegrationInstance[]
}

const FilterCategoryCpis = forwardRef<Ref, Props>(
  (
    {
      filtersOpen,
      handleCloseFilters,
      handleClickFilter,
      cpiActivationStatus,
      setCpiActivationStatus,
      cpiAssignment,
      setCpiAssignment,
      configuredIntegrations,
      setFilters,
      defaultFilters,
      supportedIntegrations
    },
    ref
  ) => {
    const uniqueConfiguredIntegrations = useMemo(
      () =>
        configuredIntegrations.reduce((accumulator: ConfiguredDataSource[], current) => {
          if (!accumulator.find((item) => item.integration_name === current.integration_name)) {
            accumulator.push(current)
          }
          return accumulator
        }, []),
      [configuredIntegrations]
    )
    const [tempFilters, setTempFilters] = useState<LibraryFilters>(defaultFilters)
    const [value, setValue] = useState<IntegrationInstance[]>([])
    const [allChecked, setAllChecked] = useState(false)

    const {
      getRootProps,
      getInputProps,
      getTagProps,
      getListboxProps,
      getOptionProps,
      groupedOptions,
      getClearProps,
      focused,
      setAnchorEl
    } = useAutocomplete({
      id: 'data-source-autocomplete-hook',
      value,
      onChange: (event, newValue) => {
        setValue(newValue)
        setTempFilters((pre) => ({
          ...pre,
          source: newValue.map((n) => n.name)
        }))
      },
      multiple: true,
      options: supportedIntegrations,
      getOptionLabel: (option) => option.name
    })

    const selectedValueNames = value.map((n) => n.name)

    const groupedFilteredOptions = ((groupedOptions as any) || []).filter(
      (n: any) => !selectedValueNames.includes(n.integration_name)
    )

    const handleStatusChange = (status: 'all' | 'active' | 'inactive') => {
      setCpiActivationStatus(status)
      setTempFilters((pre) => ({
        ...pre,
        status: [status]
      }))
    }

    const handleFrameworkCategoryChange = (category: 'all' | 'assigned' | 'unassigned') => {
      setCpiAssignment(category)
      setTempFilters((pre) => ({
        ...pre,
        frameworkCategory: category === 'all' ? '' : category
      }))
    }

    const handleApply = () => {
      setFilters(tempFilters)
      handleCloseFilters()
    }

    const handleReset = () => {
      setCpiActivationStatus('all')
      setCpiAssignment('all')
      setValue([])
      setTempFilters(defaultFilters)
      setAllChecked(false)
    }

    return (
      <>
        <ThemeButton
          themevariant="secondary"
          sx={{
            minWidth: '112px',
            background: filtersOpen ? `${theme.colors.analyzerIconActive} !important` : undefined,
            color: filtersOpen ? `${theme.colors.foreground} !important` : undefined,
            borderColor: filtersOpen ? `${theme.colors.controlBorder} !important` : undefined,
            "html[data-theme='light'] &": {
              borderColor: `${theme.colors.dropdownBorder} !important`
            },
            svg: {
              path: {
                fill: filtersOpen ? `${theme.colors.foreground} !important` : undefined
              }
            }
          }}
          ref={ref}
          id="filter-composition-button"
          aria-controls={filtersOpen ? 'filter-composition-menu' : undefined}
          aria-expanded={filtersOpen ? 'true' : undefined}
          aria-haspopup="true"
          onClick={handleClickFilter}
        >
          <>
            <FilterAltOutlined />
            Filters
          </>
        </ThemeButton>

        <Popper
          open={filtersOpen}
          anchorEl={(ref as any)?.current || null}
          placement="auto"
          modifiers={[
            {
              options: {
                altAxis: true,
                altBoundary: true,
                tether: true,
                padding: theme.spacing.medium
              }
            }
          ]}
        >
          <ClickAwayListener
            onClickAway={() => {
              handleReset()
              handleCloseFilters()
            }}
          >
            <PopperInner className="popper-inner">
              <Box className="popper-inner-wrapper">
                <Box className="popper-search-container">
                  <Typography className="header-text">Choose CPI data source</Typography>
                  <SearchDataSources
                    options={supportedIntegrations}
                    getRootProps={getRootProps}
                    getTagProps={getTagProps}
                    getListboxProps={getListboxProps}
                    getOptionProps={getOptionProps}
                    focused={focused}
                    setAnchorEl={setAnchorEl}
                    value={value}
                    groupedOptions={groupedOptions as ConfiguredDataSource[]}
                    groupedFilteredOptions={groupedFilteredOptions}
                    getInputProps={getInputProps}
                    selectedValueNames={selectedValueNames}
                    getClearProps={getClearProps}
                    setAllChecked={setAllChecked}
                    uniqueConfiguredIntegrations={uniqueConfiguredIntegrations}
                  />
                </Box>

                <FormControlLabel
                  className="all-option-form-control"
                  control={
                    <Checkbox
                      icon={
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect x="0.5" y="0.5" width="19" height="19" rx="1.5" stroke={theme.colors.controlBorder} />
                        </svg>
                      }
                      checkedIcon={<InstanceCheckedIcon />}
                      checked={allChecked}
                      value={allChecked}
                      onChange={(event, checked) => {
                        setAllChecked(checked)
                        const newSources = checked
                          ? supportedIntegrations.filter((c) => {
                              const found = configuredIntegrations.find((n) => n.integration_name === c.name)
                              return !!found
                            })
                          : []
                        setTempFilters((pre) => ({
                          ...pre,
                          source: newSources.map((c) => c.name)
                        }))
                        setValue(newSources)
                      }}
                      inputProps={{ 'aria-label': 'controlled' }}
                    />
                  }
                  label="All Active Data Sources"
                />

                <FormControl className="checkbox-form-control" component="fieldset" variant="standard">
                  <FormLabel component="legend">Select CPI activation status:</FormLabel>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={cpiActivationStatus === 'all'}
                          onChange={() => handleStatusChange('all')}
                          checkedIcon={<CheckedIcon />}
                          icon={<UncheckedIcon />}
                        />
                      }
                      label="All"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={cpiActivationStatus === 'active'}
                          onChange={() => handleStatusChange('active')}
                          checkedIcon={<CheckedIcon />}
                          icon={<UncheckedIcon />}
                        />
                      }
                      label="Active"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={cpiActivationStatus === 'inactive'}
                          onChange={() => handleStatusChange('inactive')}
                          checkedIcon={<CheckedIcon />}
                          icon={<UncheckedIcon />}
                        />
                      }
                      label="Inactive"
                    />
                  </FormGroup>
                </FormControl>

                <FormControl className="checkbox-form-control" component="fieldset" variant="standard">
                  <FormLabel component="legend">Choose if CPI is assigned to a framework category:</FormLabel>
                  <FormGroup>
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={cpiAssignment === 'all'}
                          onChange={() => handleFrameworkCategoryChange('all')}
                          checkedIcon={<CheckedIcon />}
                          icon={<UncheckedIcon />}
                        />
                      }
                      label="All"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={cpiAssignment === 'assigned'}
                          onChange={() => handleFrameworkCategoryChange('assigned')}
                          checkedIcon={<CheckedIcon />}
                          icon={<UncheckedIcon />}
                        />
                      }
                      label="Assigned"
                    />
                    <FormControlLabel
                      control={
                        <Checkbox
                          checked={cpiAssignment === 'unassigned'}
                          onChange={() => handleFrameworkCategoryChange('unassigned')}
                          checkedIcon={<CheckedIcon />}
                          icon={<UncheckedIcon />}
                        />
                      }
                      label="Unassigned"
                    />
                  </FormGroup>
                </FormControl>
                {/* </Box> */}

                <Box className="popper-footer">
                  <ThemeButton themevariant="tertiary" onClick={handleReset}>
                    <>
                      <ResetIcon />
                      Reset
                    </>
                  </ThemeButton>
                  <Box className="footer-actions-wrapper">
                    <ThemeButton
                      themevariant="tertiary"
                      onClick={() => {
                        handleReset()
                        handleCloseFilters()
                      }}
                    >
                      Cancel
                    </ThemeButton>
                    <ThemeButton themevariant="primary" sx={{ minWidth: '95px' }} onClick={handleApply}>
                      Apply
                    </ThemeButton>
                  </Box>
                </Box>
              </Box>
            </PopperInner>
          </ClickAwayListener>
        </Popper>
      </>
    )
  }
)

export default FilterCategoryCpis
